'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'

import CustomCalendar from '@/components/dev/customCalendar'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

type DateBoundary =
  | 'today'
  | {
      yearsAgo: number
    }

interface DateFieldProps {
  name: string
  label: string
  required?: boolean
  placeholder?: string

  /**
   * Serializable date constraints so this client component
   * can safely be rendered from a Server Component.
   *
   * Examples:
   *
   * minDate={{ yearsAgo: 100 }}
   * maxDate={{ yearsAgo: 18 }}
   *
   * minDate="today"
   */
  minDate?: DateBoundary
  maxDate?: DateBoundary

  /**
   * Useful for future-date fields such as Preferred Start Date.
   * Shows the existing CustomCalendar past-date treatment.
   */
  showPastCross?: boolean

  /**
   * Optional initial YYYY-MM-DD value.
   */
  defaultValue?: string

  disabled?: boolean
}

function startOfLocalDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function resolveBoundary(boundary?: DateBoundary): Date | undefined {
  if (!boundary) return undefined

  const today = startOfLocalDay(new Date())

  if (boundary === 'today') {
    return today
  }

  const result = new Date(today)
  result.setFullYear(result.getFullYear() - boundary.yearsAgo)

  return result
}

function parseISODate(value?: string): Date | null {
  if (!value) return null

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  const date = new Date(year, month - 1, day)
  date.setHours(0, 0, 0, 0)

  // Reject impossible dates such as 2026-02-31.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }

  return date
}

function toISODate(date: Date | null): string {
  if (!date) return ''

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function formatDisplayDate(date: Date): string {
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const DateField: React.FC<DateFieldProps> = ({
  name,
  label,
  required = false,
  placeholder = 'Select a date',
  minDate,
  maxDate,
  showPastCross = false,
  defaultValue,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false)

  const [selectedDate, setSelectedDate] = useState<Date | null>(() =>
    parseISODate(defaultValue),
  )

  const resolvedMinDate = useMemo(() => resolveBoundary(minDate), [minDate])
  const resolvedMaxDate = useMemo(() => resolveBoundary(maxDate), [maxDate])

  const value = toISODate(selectedDate)

  const hiddenInputRef = useRef<HTMLInputElement>(null)

  // React updating a hidden input's value does not emit a native bubbling input
  // event, so dispatch one whenever the canonical value changes. This lets
  // FormClient's delegated input/change validation re-run for this field.
  useEffect(() => {
    const input = hiddenInputRef.current
    if (!input) return

    input.dispatchEvent(
      new Event('input', {
        bubbles: true,
      }),
    )
  }, [value])

  return (
    <div className="grid gap-1.5 sm:gap-2">
      <Label htmlFor={`${name}-trigger`} className="text-sm">
        {label}

        {required && (
          <span className="text-red-500" aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </Label>

      {/*
       * Keep the canonical value as a normal named form control.
       *
       * FormClient and FormData can therefore continue treating
       * this field exactly like the native date input it replaces.
       */}
      <input
        ref={hiddenInputRef}
        type="hidden"
        name={name}
        value={value}
        required={required}
        disabled={disabled}
        readOnly
      />

      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (!disabled) setOpen(nextOpen)
        }}
      >
        <PopoverTrigger asChild>
          <button
            id={`${name}-trigger`}
            data-field-trigger={name}
            type="button"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            className={[
              'flex h-10 w-full items-center justify-between rounded-md',
              'border border-input bg-background px-3 text-left text-sm',
              'shadow-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
            ].join(' ')}
          >
            <span
              className={
                selectedDate ? 'text-foreground' : 'text-muted-foreground'
              }
            >
              {selectedDate
                ? formatDisplayDate(selectedDate)
                : placeholder}
            </span>

            <CalendarIcon
              className="h-4 w-4 shrink-0 opacity-60"
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-[min(24rem,calc(100vw-2rem))] max-w-none p-2"
        >
          <CustomCalendar
            className="w-full"
            selected={selectedDate}
            onChange={(date) => {
              setSelectedDate(startOfLocalDay(date))
              setOpen(false)
            }}
            minDate={resolvedMinDate}
            maxDate={resolvedMaxDate}
            showPastCross={showPastCross}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default DateField
